import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Rollen mit deutschen Labels
const ROLES = [
  { value: "superadmin", label: "Super-Admin", color: "bg-violet-100 text-violet-700" },
  { value: "admin", label: "Admin", color: "bg-blue-100 text-blue-700" },
  { value: "trainer", label: "Trainer", color: "bg-emerald-100 text-emerald-700" },
  { value: "mitarbeiter", label: "Mitarbeiter", color: "bg-slate-100 text-slate-600" },
];

export function UsersTabModern({ authToken, groups = [], isSuperAdmin }) {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const [form, setForm] = useState({
    email: "",
    full_name: "",
    role: "trainer",
    password: "",
    allowed_group_ids: [],
    is_active: true,
  });

  function authHeaders() {
    return authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
  }

  async function loadUsers() {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/users`, authHeaders());
      setUsers(res.data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Fehler beim Laden der Benutzer", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split(".")[1] || ""));
        if (payload?.sub) setCurrentUserId(payload.sub);
      } catch {}
    }
  }, [authToken]);

  const resetForm = () => {
    setForm({ email: "", full_name: "", role: "trainer", password: "", allowed_group_ids: [], is_active: true });
    setEditingUser(null);
    setShowForm(false);
  };

  const startEdit = (user) => {
    setForm({
      email: user.email || "",
      full_name: user.full_name || "",
      role: user.role || "trainer",
      password: "",
      allowed_group_ids: Array.isArray(user.allowed_group_ids) ? user.allowed_group_ids : [],
      is_active: user.is_active !== false,
    });
    setEditingUser(user);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name) {
      toast({ title: "Bitte E-Mail und Name ausfüllen", variant: "destructive" });
      return;
    }
    if (!editingUser && !form.password) {
      toast({ title: "Bitte Passwort angeben", variant: "destructive" });
      return;
    }

    try {
      const payload = {
        email: form.email,
        full_name: form.full_name,
        role: form.role,
        password: form.password || undefined,
        allowed_group_ids: form.role === "trainer" ? form.allowed_group_ids : null,
        is_active: form.is_active,
      };

      if (editingUser) {
        await axios.put(`${API_BASE}/api/users/${editingUser.id}`, payload, authHeaders());
        toast({ title: "Benutzer aktualisiert" });
      } else {
        await axios.post(`${API_BASE}/api/users`, payload, authHeaders());
        toast({ title: "Benutzer angelegt" });
      }
      loadUsers();
      resetForm();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Speichern";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleDelete = async (user) => {
    try {
      await axios.delete(`${API_BASE}/api/users/${user.id}`, authHeaders());
      toast({ title: "Benutzer gelöscht" });
      loadUsers();
      setDeleteConfirm(null);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Löschen";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API_BASE}/api/users/${user.id}`, { is_active: !user.is_active }, authHeaders());
      toast({ title: user.is_active !== false ? "Benutzer deaktiviert" : "Benutzer aktiviert" });
      loadUsers();
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || "Fehler beim Ändern des Status";
      toast({ title: msg, variant: "destructive" });
    }
  };

  const getRoleInfo = (role) => ROLES.find((r) => r.value === role) || ROLES[3];

  const getGroupColor = (groupId) => {
    const group = groups.find((g) => g.id === groupId);
    return group?.color || "#e5e7eb";
  };

  const getTextColor = (bgColor) => {
    if (!bgColor?.startsWith("#")) return "#1f2937";
    try {
      const r = parseInt(bgColor.slice(1, 3), 16);
      const g = parseInt(bgColor.slice(3, 5), 16);
      const b = parseInt(bgColor.slice(5, 7), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? "#1f2937" : "#ffffff";
    } catch {
      return "#1f2937";
    }
  };

  // Statistiken
  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active !== false).length;
    const byRole = ROLES.map((r) => ({
      ...r,
      count: users.filter((u) => u.role === r.value).length,
    }));
    return { total, active, inactive: total - active, byRole };
  }, [users]);

  if (!isSuperAdmin) {
    return (
      <div className="rounded-xl bg-amber-50 p-6 text-center">
        <p className="text-sm text-amber-700">Nur Super-Administratoren können Benutzer verwalten.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="users-tab-modern">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white p-4 shadow-sm">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Benutzerverwaltung</h2>
          <p className="text-sm text-slate-500">{stats.total} Benutzer, {stats.active} aktiv</p>
        </div>
        <div className="flex items-center gap-3">
          {stats.byRole.map((r) => (
            <div key={r.value} className="text-center">
              <div className={`rounded-full px-2 py-0.5 text-xs font-medium ${r.color}`}>{r.count}</div>
              <div className="text-xs text-slate-500 mt-0.5">{r.label}</div>
            </div>
          ))}
        </div>
        <button
          data-testid="new-user-button"
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${
            showForm && !editingUser ? "bg-slate-900 text-white" : "bg-violet-100 text-violet-700 hover:bg-violet-200"
          }`}
        >
          {showForm && !editingUser ? "Abbrechen" : "+ Neuer Benutzer"}
        </button>
      </div>

      {/* Lösch-Bestätigung */}
      {deleteConfirm && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-rose-100">
              <svg className="h-5 w-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-rose-900">Benutzer löschen?</h4>
              <p className="mt-1 text-xs text-rose-700">
                Möchten Sie <strong>"{deleteConfirm.full_name || deleteConfirm.email}"</strong> wirklich löschen?
              </p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setDeleteConfirm(null)} className="rounded-full bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50">
                  Abbrechen
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500">
                  Ja, löschen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Formular */}
      {showForm && (
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-white p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-violet-900">
            {editingUser ? `"${editingUser.full_name || editingUser.email}" bearbeiten` : "Neuen Benutzer anlegen"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs text-slate-500">E-Mail *</label>
                <input
                  type="email"
                  data-testid="user-email-input"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Name *</label>
                <input
                  type="text"
                  data-testid="user-name-input"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  required
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-xs text-slate-500">Rolle</label>
                <select
                  data-testid="user-role-select"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-slate-500">Passwort {editingUser ? "(leer = unverändert)" : "*"}</label>
                <input
                  type="password"
                  data-testid="user-password-input"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  required={!editingUser}
                  className="w-full rounded-lg border-0 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 shadow-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700">Aktiv</span>
                </label>
              </div>
            </div>

            {/* Gruppen-Zuordnung für Trainer */}
            {form.role === "trainer" && (
              <div>
                <label className="mb-2 block text-xs text-slate-500">Zugriff auf Gruppen</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, allowed_group_ids: [] }))}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                      form.allowed_group_ids.length === 0 ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    Alle Gruppen
                  </button>
                  {groups.map((g) => {
                    const isSelected = form.allowed_group_ids.includes(g.id);
                    const bg = isSelected ? g.color || "#e5e7eb" : "#f1f5f9";
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setForm((f) => ({
                          ...f,
                          allowed_group_ids: isSelected
                            ? f.allowed_group_ids.filter((id) => id !== g.id)
                            : [...f.allowed_group_ids, g.id],
                        }))}
                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${isSelected ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                        style={{ backgroundColor: bg, color: getTextColor(bg) }}
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-slate-400">Keine Auswahl = Zugriff auf alle Gruppen</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-2">
              <button type="button" onClick={resetForm} className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200">
                Abbrechen
              </button>
              <button type="submit" data-testid="user-submit-button" className="rounded-full bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500">
                {editingUser ? "Speichern" : "Benutzer anlegen"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Benutzer-Liste */}
      <div className="space-y-2" data-testid="users-list">
        {loading ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <div className="h-6 w-6 mx-auto animate-spin rounded-full border-2 border-violet-600 border-t-transparent" />
            <p className="mt-2 text-sm text-slate-500">Lade Benutzer...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <div className="mb-2 text-4xl">👥</div>
            <p className="text-sm text-slate-500">Noch keine Benutzer angelegt.</p>
          </div>
        ) : (
          users.map((user) => {
            const roleInfo = getRoleInfo(user.role);
            const isSelf = currentUserId && user.id === currentUserId;
            const isInactive = user.is_active === false;
            const trainerGroups = user.role === "trainer" && Array.isArray(user.allowed_group_ids) && user.allowed_group_ids.length > 0
              ? groups.filter((g) => user.allowed_group_ids.includes(g.id))
              : [];

            // "Zuletzt online" formatieren
            const lastOnlineText = user.last_online 
              ? formatDistanceToNow(parseISO(user.last_online), { addSuffix: true, locale: de })
              : null;

            return (
              <div
                key={user.id}
                data-testid={`user-card-${user.id}`}
                className={`rounded-xl bg-white p-4 shadow-sm transition-all ${
                  isInactive ? "opacity-60" : ""
                } ${editingUser?.id === user.id ? "ring-2 ring-violet-400" : ""}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  {/* Info */}
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${roleInfo.color}`}>
                      {(user.full_name || user.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900" style={{ fontSize: 'var(--font-body-size)' }}>
                          {user.full_name || "–"}
                        </span>
                        {isSelf && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-xs font-semibold text-sky-700">Du</span>}
                        {isInactive && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs font-semibold text-slate-600">Deaktiviert</span>}
                      </div>
                      <div className="text-slate-500" style={{ fontSize: 'var(--font-small-size)' }}>{user.email}</div>
                      
                      {/* Zuletzt online */}
                      {lastOnlineText && (
                        <div className="flex items-center gap-1 mt-1 text-slate-400" style={{ fontSize: 'var(--font-small-size)' }}>
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Zuletzt online: {lastOnlineText}</span>
                        </div>
                      )}

                      {/* Rolle & Gruppen - linksbündig */}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${roleInfo.color}`}>
                          {roleInfo.label}
                        </span>
                        {user.role === "trainer" && (
                          <>
                            {trainerGroups.length === 0 ? (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Alle Gruppen</span>
                            ) : (
                              trainerGroups.slice(0, 3).map((g) => (
                                <span
                                  key={g.id}
                                  className="rounded-full px-2 py-0.5 text-xs font-medium"
                                  style={{ backgroundColor: g.color || "#e5e7eb", color: getTextColor(g.color || "#e5e7eb") }}
                                >
                                  {g.name}
                                </span>
                              ))
                            )}
                            {trainerGroups.length > 3 && (
                              <span className="text-xs text-slate-400">+{trainerGroups.length - 3}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Aktionen */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      data-testid={`toggle-user-${user.id}`}
                      onClick={() => handleToggleActive(user)}
                      disabled={isSelf}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                        isInactive
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : "bg-slate-100 text-slate-600 hover:bg-amber-100 hover:text-amber-700"
                      } disabled:opacity-50`}
                    >
                      {isInactive ? "Aktivieren" : "Deaktivieren"}
                    </button>
                    <button
                      data-testid={`edit-user-${user.id}`}
                      onClick={() => startEdit(user)}
                      className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200"
                    >
                      Bearbeiten
                    </button>
                    <button
                      data-testid={`delete-user-${user.id}`}
                      onClick={() => setDeleteConfirm(user)}
                      disabled={isSelf}
                      className="rounded-lg px-2 py-1.5 text-xs text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
